import ee from '../../services/earth-engine';
import { REDUCERS } from '../utils/index.js';

/**
 * @function getTrainingDepthReducer
 * @description The `ee.Reducer` behind the label chosen on the AOI step.
 * @param {String} reducerLabel - Lowercased label, as `REDUCERS` names them.
 * @memberof module:Bathymetry
 * @returns {Object|undefined} The reducer, or undefined when the label is unknown.
 */
export const getTrainingDepthReducer = (reducerLabel) => REDUCERS.find((r) => r.label === reducerLabel)?.func?.();

/**
 * @function addPredDepthStd
 * @description Guarantees a `pred_depth` band, taking the first of `pred_depth_std`,
 * `pred_depth`, `pred_depth_raw` or `depth` the image carries. Each model writes its result under
 * a slightly different name; when none is there the first band is selected, so the mismatch fails
 * downstream instead of silently returning the wrong surface.
 * @param {Object} img - Any model output, as an `ee.Image`.
 * @memberof module:Bathymetry
 * @returns {Object} The image with `pred_depth` present.
 */
export const addPredDepthStd = (img) => {
  const names = img.bandNames();
  const priorityList = ee.List(['pred_depth_std', 'pred_depth', 'pred_depth_raw', 'depth']);
  const existingPriority = priorityList.filter(ee.Filter.inList('item', names));
  const depthBandName = ee.String(
    ee.Algorithms.If(existingPriority.size().gt(0), existingPriority.get(0), names.get(0)),
  );
  return img.addBands(img.select([depthBandName]).rename('pred_depth'), null, true);
};

/**
 * @function trainingCollection
 * @description The soundings from the training CSV as an `ee.FeatureCollection`, built on the
 * server from one list of numbers.
 * Built in the browser, each point travelled as an `ee.Feature` of its own - around 480 bytes of
 * request per point, repeated in every call that used the survey. A 175 908-point grid reached
 * 85 MB and Earth Engine refused it ("Total request size ... must be less than or equal to
 * 50331648 bytes") at the first step that touched it, whatever the size of the area. As a plain
 * list of `[x, y, depth]` the same grid is about 6 MB.
 * The list goes as one JSON string, decoded on the server: written out as a list, the client
 * encoded each number as a node of its own, 2.6 s on the page's thread for every 20 000 points
 * of every request; as a string the same points take 22 ms.
 * @param {Array<Array<Number>>} matrix - The soundings, as `[x, y, depth]` rows.
 * @param {String} [crs] - The CRS of `x` and `y`; WGS84 when it names EPSG:4326 or is missing.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.FeatureCollection` of points carrying `or_depth`.
 */
export const trainingCollection = (matrix, crs) => {
  const projection = crs && crs.indexOf('4326') === -1 ? crs : null;
  return ee.FeatureCollection(
    ee.List(ee.String(JSON.stringify(matrix)).decodeJSON()).map((row) => {
      const values = ee.List(row);
      return ee.Feature(ee.Geometry.Point([values.get(0), values.get(1)], projection), { or_depth: values.get(2) });
    }),
  );
};

/**
 * @function observedDepthImage
 * @description Rasterises the surveyed soundings into an `or_depth` band clipped to the area, so
 * the measured depth can be paired pixel by pixel with the modelled one. Without a survey or a
 * reducer it returns a fully masked band, which pairs with nothing rather than pairing with zero.
 * @param {Object} [matrix] - The soundings, as an `ee.FeatureCollection`.
 * @param {Object} [reducer] - How several soundings in one pixel are combined.
 * @param {Object} region - The area to clip to, as an `ee.Geometry`.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Image` carrying `or_depth`.
 */
export const observedDepthImage = (matrix, reducer, region) =>
  matrix && reducer
    ? matrix
        .reduceToImage({ properties: ['or_depth'], reducer })
        .rename('or_depth')
        .toFloat()
        .selfMask()
        .clip(region)
    : ee.Image(0).updateMask(ee.Image(0)).rename('or_depth');
